/**
 * 加购后自动应用 discountbar 折扣码（方案 A）
 * - 有码加购：hold 抽屉 → 带码 → 单次 morph → 再打开
 * - merge 时保留车上原有的码（含用户手输、暂未达门槛的）；仅在超上限或单码兜底成功时才覆盖：
 *   多码组合失败后会退回「只带商品码」再试一次，成功就接受挤掉用户原有的码。
 *   这是刻意的取舍——宁可让本次加购的商品折扣真的生效，也不要出现「带了码却没优惠」。
 * - 失败只按网络类错误做冷却（networkFailUntil），业务 not_applicable 不冷却，避免误伤重试
 *
 * 注：applicable:false 的码不会渲染成 pill、也不参与任何金额计算，
 * 属于对用户不可见的状态，因此不主动清理，避免误删用户自己的码。
 */
import { ThemeEvents, DiscountUpdateEvent, CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { rebindCartRecommendedScroll } from '@theme/ug-cart-recommended';

const SOURCE_ID = 'ug-auto-discount';
/** 仅网络类失败冷却；业务 not_applicable 不挡下一轮加购 */
const NETWORK_FAIL_COOLDOWN_MS = 2500;
/**
 * hold 抽屉的兜底上限。
 * ATC 的 "Added" 动画只有 2 秒，兜底再长用户就会以为加购失败而反复点、产生重复行。
 * 超时先把抽屉开出来给反馈，带码结果回来后再 morph 一次。
 */
const HOLD_TIMEOUT_MS = 2200;
/** hold 不再按码数拉长，避免超过 Added 2s 窗口后用户连点 */
/** Shopify 多码过多时易出现部分 applicable:false；与手动体验对齐做上限 */
const MAX_APPLICABLE_CODES = 5;
const RETRY_DELAY_MS = 400;

/** @type {Promise<void>} */
let queueTail = Promise.resolve();

/** hold 世代号：避免超时兜底与正常完成互相覆盖 */
let holdGeneration = 0;

/** @type {Map<string, number>} */
const networkFailUntil = new Map();

/** hold 期间被禁用的 ATC / Buy Now，hold 结束后要还原 @type {{ button: HTMLButtonElement, wasDisabled: boolean }[]} */
let heldPurchaseButtons = [];

function getBridge() {
  if (!window.UGAutoDiscountBridge) {
    window.UGAutoDiscountBridge = {
      holdDrawer: false,
      /** hold 期间车内容是否被本模块以外的动作改过 */
      dirtyDuringHold: false,
    };
  }
  return window.UGAutoDiscountBridge;
}

/** hold 期间车被别人改过：结束时不能再用加购那一刻的旧快照 morph */
function markHoldCartDirty() {
  getBridge().dirtyDuringHold = true;
}

function clearHoldCartDirty() {
  getBridge().dirtyDuringHold = false;
}

function isEnabled() {
  return Boolean(window.UG_AUTO_DISCOUNT?.enabled);
}

/** @param {string} code */
function normalizeCode(code) {
  return String(code || '')
    .trim()
    .toLowerCase();
}

/** @param {string} code */
function isInNetworkCooldown(code) {
  const until = networkFailUntil.get(normalizeCode(code));
  return typeof until === 'number' && Date.now() < until;
}

/** @param {string} code */
function markNetworkFail(code) {
  networkFailUntil.set(normalizeCode(code), Date.now() + NETWORK_FAIL_COOLDOWN_MS);
}

/** @param {string} code */
function clearNetworkFail(code) {
  networkFailUntil.delete(normalizeCode(code));
}

/** @returns {string[]} */
function collectCartSectionIds() {
  /** @type {Set<string>} */
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) {
      ids.add(el.dataset.sectionId);
    }
  });
  return [...ids];
}

/**
 * 重新向服务端拉取并渲染所有购物车 section。
 * @returns {Promise<void>}
 */
async function renderAllCartSections() {
  const ids = collectCartSectionIds();
  if (!ids.length) return;

  await Promise.all(ids.map((id) => sectionRenderer.renderSection(id, { cache: false })));
  rebindCartRecommendedScroll();
}

/**
 * @param {Element | null | undefined} el
 * @returns {string}
 */
function getCodeFromElement(el) {
  if (!(el instanceof HTMLElement)) return '';
  const host = el.closest('[data-ug-discount-code]');
  if (!(host instanceof HTMLElement)) return '';
  return (host.dataset.ugDiscountCode || '').trim();
}

/**
 * 加购事件上的码：主商品 + 勾选推荐。没有 extra 时回退到表单 data。
 * @param {any} detail
 * @param {Element | null} target
 * @returns {string[]}
 */
function collectAddCodes(detail, target) {
  /** @type {string[]} */
  const fromEvent = Array.isArray(detail?.data?.discountCodes) ? detail.data.discountCodes : [];
  const codes = fromEvent
    .map((code) => String(code || '').trim())
    .filter(Boolean);

  if (!codes.length) {
    const main = getCodeFromElement(target);
    if (main) codes.push(main);
  }

  const seen = new Set();
  return codes.filter((code) => {
    const key = normalizeCode(code);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * 多码一次 merge 写入。整批都不生效时再退回逐个 applyProductCode。
 * 部分成功时不再对失败码做「只带单码」兜底，避免把本批已生效的兄弟码挤掉。
 * @param {string[]} codes
 * @param {{ force?: boolean, isCurrent?: () => boolean }} [options]
 * @returns {Promise<boolean>} 是否已派发 DiscountUpdateEvent 刷过 UI。业务上是否带码成功看 GA4 上报，不看这个值
 */
async function applyProductCodes(codes, options = {}) {
  const list = (codes || []).map((code) => String(code || '').trim()).filter(Boolean);
  if (!list.length) return false;
  if (list.length === 1) return applyProductCode(list[0], options);

  const isCurrent = typeof options.isCurrent === 'function' ? options.isCurrent : () => true;
  const state = await fetchCartDiscountState();

  /** @type {string[]} */
  const pending = [];
  for (const code of list) {
    if (state.applicableMap[normalizeCode(code)] === true) {
      clearNetworkFail(code);
      trackAutoDiscountGa4({ isSuccess: true, coupon: code, reason: 'already_applicable' });
    } else {
      pending.push(code);
    }
  }
  // 码本来就生效，但这里没派发过事件，UI 还是旧的，必须让调用方自己去刷
  if (!pending.length) return false;

  let merged = state.codes.slice();
  for (const code of pending) {
    merged = buildMergedCodes(merged, state.applicableMap, code);
  }

  let data = await postCartDiscount(merged);
  const countOk = () => pending.filter((code) => validateDiscountResponse(data, code).ok).length;
  if (!countOk()) {
    await delay(RETRY_DELAY_MS);
    data = await postCartDiscount(merged);
  }

  // 整批都不生效：退回单码逻辑（含「只带商品码」兜底）
  if (!countOk()) {
    let refreshed = false;
    for (const code of pending) {
      if (await applyProductCode(code, options)) refreshed = true;
    }
    return refreshed;
  }

  let anyOk = false;
  for (const code of pending) {
    const result = validateDiscountResponse(data, code);
    if (result.ok) {
      clearNetworkFail(code);
      trackAutoDiscountGa4({ isSuccess: true, coupon: code, reason: 'applied' });
      anyOk = true;
    } else {
      trackAutoDiscountGa4({
        isSuccess: false,
        coupon: code,
        reason: result.reason || 'not_applicable',
      });
    }
  }

  if (!anyOk || !isCurrent()) return false;
  document.dispatchEvent(new DiscountUpdateEvent(data, SOURCE_ID));
  return true;
}

/**
 * 用手上的 section 快照 morph 购物车；没有快照就重新拉一次。
 * @param {Record<string, string> | null | undefined} sections
 * @returns {Promise<void>}
 */
async function morphFromSections(sections) {
  if (!sections || typeof sections !== 'object') {
    // 兜底必须真的重拉 section：rebindCartRecommendedScroll 只重绑推荐位横滑，不刷新购物车
    await renderAllCartSections();
    return;
  }

  for (const sectionId of Object.keys(sections)) {
    const html = sections[sectionId];
    if (!html) continue;
    try {
      // morphSection 是 async 且会 throw，同步调用会变成 unhandled rejection
      await morphSection(sectionId, html);
    } catch (error) {
      console.error('[ug-auto-discount] section morph 失败', sectionId, error);
    }
  }
  rebindCartRecommendedScroll();
}

/**
 * hold 结束时刷新购物车。
 * cartAddSections 是加购那一刻的服务端快照，只要车在 hold 期间被动过，
 * morph 回去就会把用户的改动整体回滚，只能重新拉最新 section。
 * @param {Record<string, string> | undefined} cartAddSections
 * @param {boolean} canUseSnapshot - 超时兜底已经放开 hold（用户又能操作购物车了）时传 false
 * @returns {Promise<void>}
 */
async function refreshCartForHoldEnd(cartAddSections, canUseSnapshot) {
  if (!canUseSnapshot || getBridge().dirtyDuringHold) {
    await renderAllCartSections();
    return;
  }
  await morphFromSections(cartAddSections);
}

function openCartDrawer() {
  const drawer = document.querySelector('cart-drawer-component');
  if (drawer instanceof HTMLElement && drawer.hasAttribute('auto-open')) {
    const anyDrawer = /** @type {{ showDialog?: () => void }} */ (drawer);
    if (typeof anyDrawer.showDialog === 'function') {
      anyDrawer.showDialog();
    }
  }
}

/**
 * hold 期间禁用发起本次加购的 ATC 按钮。
 * 抽屉要等带码结果才打开，中间这段空窗用户会以为加购失败而反复点，产生重复行。
 * @param {Element | null} target - 派发 cart:update 的 product-form-component
 */
function holdAddToCartButton(target) {
  releaseAddToCartButton();
  if (!(target instanceof Element)) return;

  const section = target.closest('.shopify-section') || target;
  section.querySelectorAll('[ref="addToCartButton"], .ug-buy-now-button').forEach((button) => {
    if (!(button instanceof HTMLButtonElement)) return;
    heldPurchaseButtons.push({ button, wasDisabled: button.disabled });
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
  });
}

/** hold 结束：把 ATC / Buy Now 恢复成 hold 之前的状态（缺货时本来就是禁用的，不能一律解禁） */
function releaseAddToCartButton() {
  const held = heldPurchaseButtons;
  heldPurchaseButtons = [];
  held.forEach(({ button, wasDisabled }) => {
    button.disabled = wasDisabled;
    button.removeAttribute('aria-busy');
  });
}

function releaseHoldAndOpen() {
  getBridge().holdDrawer = false;
  releaseAddToCartButton();
  openCartDrawer();
}

/** @param {number} ms */
function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * GA4：自动带码成功/失败（走全站 ugGa4Track → Shopify.analytics.publish）
 * @param {{ isSuccess: boolean, coupon: string, reason?: string }} payload
 */
function trackAutoDiscountGa4(payload) {
  try {
    const track = window.ugGa4Track?.autoDiscountEvent;
    if (typeof track === 'function') {
      track({
        isSuccess: Boolean(payload.isSuccess),
        coupon: payload.coupon,
        reason: payload.reason,
        position: 'ug-auto-discount',
      });
      return;
    }
    // 兜底：ugGa4Track 尚未注入时直发
    if (window.Shopify?.analytics?.publish) {
      const code = String(payload.coupon || '').trim();
      const reason = String(payload.reason || (payload.isSuccess ? 'applied' : 'unknown'));
      const status = payload.isSuccess
        ? 'Success'
        : reason === 'not_applicable'
          ? 'Skipped'
          : 'Failure';
      window.Shopify.analytics.publish('ga4Event', {
        log_name: `自动带码：${status}; 码：${code}; 原因：${reason};`,
        event_name: 'auto_discount',
        event_parameters: {
          status,
          coupon: code,
          reason,
          position: 'ug-auto-discount',
        },
      });
    }
  } catch {
    /* 埋点失败不影响带码主流程 */
  }
}

/**
 * 带一次网络重试，缓解闲置后空响应
 * @param {() => Promise<Response>} request
 * @returns {Promise<Response>}
 */
async function fetchWithRetry(request) {
  try {
    const response = await request();
    if (!response.ok && response.status >= 500) {
      await delay(RETRY_DELAY_MS);
      return request();
    }
    return response;
  } catch (error) {
    await delay(RETRY_DELAY_MS);
    return request();
  }
}

/**
 * @returns {Promise<{ codes: string[], applicableMap: Record<string, boolean> }>}
 */
async function fetchCartDiscountState() {
  const cartUrl = `${Theme.routes.cart_url}.js`;
  const response = await fetchWithRetry(() =>
    fetch(cartUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
  );
  if (!response.ok) throw new Error(`cart.js ${response.status}`);

  const text = await response.text();
  let cart;
  try {
    cart = JSON.parse(text);
  } catch {
    throw new Error('cart.js invalid json');
  }

  /** @type {string[]} */
  const codes = [];
  /** @type {Record<string, boolean>} */
  const applicableMap = {};

  for (const entry of cart.discount_codes || []) {
    if (!entry?.code) continue;
    codes.push(String(entry.code));
    applicableMap[normalizeCode(entry.code)] = Boolean(entry.applicable);
  }

  return { codes, applicableMap };
}

/**
 * 车上原有的码全部保留（用户手输的、暂未达门槛的都算），末尾追加商品码。
 * cart/update 是全量覆盖，漏掉一个就等于替用户删码，所以只在超上限时才挤：
 * 先挤当前不生效的旧码，再挤最旧的有效码，商品码本身永不被挤。
 * @param {string[]} codes
 * @param {Record<string, boolean>} applicableMap
 * @param {string} productCode
 * @returns {string[]}
 */
function buildMergedCodes(codes, applicableMap, productCode) {
  const product = productCode.trim();
  const productKey = normalizeCode(product);
  /** @type {string[]} */
  const merged = [];
  /** @type {Set<string>} */
  const seen = new Set();

  for (const c of codes) {
    const key = normalizeCode(c);
    if (!key || seen.has(key) || key === productKey) continue;
    seen.add(key);
    merged.push(c);
  }
  if (product) merged.push(product);

  while (merged.length > MAX_APPLICABLE_CODES) {
    const evictable = product ? merged.length - 1 : merged.length;
    let victim = merged.findIndex((c, i) => i < evictable && applicableMap[normalizeCode(c)] !== true);
    if (victim < 0) victim = 0;
    merged.splice(victim, 1);
  }

  return merged;
}

/**
 * @param {any} data
 * @param {string} expectedCode
 * @returns {{ ok: boolean, reason?: string }}
 */
function validateDiscountResponse(data, expectedCode) {
  const list = data?.discount_codes;
  if (!Array.isArray(list)) return { ok: false, reason: 'missing_payload' };
  const entry = list.find((d) => normalizeCode(d?.code) === normalizeCode(expectedCode));
  if (!entry) return { ok: false, reason: 'missing' };
  if (entry.applicable !== true) return { ok: false, reason: 'not_applicable' };
  return { ok: true };
}

/**
 * @param {string[]} codes
 * @returns {Promise<any>}
 */
async function postCartDiscount(codes) {
  const sections = collectCartSectionIds();
  const body = {
    discount: codes.join(','),
    ...(sections.length ? { sections } : {}),
  };
  const config = fetchConfig('json', { body: JSON.stringify(body) });
  const response = await fetchWithRetry(() =>
    fetch(Theme.routes.cart_update_url, {
      ...config,
      credentials: 'same-origin',
    })
  );
  if (!response.ok) throw new Error(`cart/update ${response.status}`);

  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch {
    throw new Error('cart/update invalid json');
  }
}

/**
 * 把商品码写到服务端购物车上。
 *
 * isCurrent 只用来拦「写 UI」的动作（派发 DiscountUpdateEvent）：
 * 带码本身是服务端写入，排队期间又发生了新一轮加购也照样要写完，
 * 否则先排队的那个码会一次都没尝试过（多码合并本就支持一次带多个码）。
 *
 * @param {string} code
 * @param {{ force?: boolean, isCurrent?: () => boolean }} [options]
 * @returns {Promise<boolean>} true = 已通过 DiscountUpdateEvent 刷新过 UI
 */
async function applyProductCode(code, options = {}) {
  const trimmed = String(code || '').trim();
  if (!trimmed) return false;

  const key = normalizeCode(trimmed);
  const isCurrent = typeof options.isCurrent === 'function' ? options.isCurrent : () => true;

  if (!options.force) {
    // 加购会改车内容，业务失败不应长冷却；仅挡短时网络雪崩
    if (isInNetworkCooldown(trimmed)) return false;
  }

  const state = await fetchCartDiscountState();

  if (state.applicableMap[key] === true) {
    clearNetworkFail(trimmed);
    trackAutoDiscountGa4({ isSuccess: true, coupon: trimmed, reason: 'already_applicable' });
    // 同上：码已生效不等于 UI 已刷新，返回 true 会让调用方跳过刷新、开出上一次的购物车
    return false;
  }

  const next = buildMergedCodes(state.codes, state.applicableMap, trimmed);
  /**
   * 本轮之前车上就有的码，单码兜底失败时要原样还回去。
   * 必须取自 cart.js 原始快照：next 已被 buildMergedCodes 挤压过，
   * 车上满 5 个码时拿它还原会替用户永久删掉被挤掉的那个。
   */
  const prior = state.codes.filter((c) => normalizeCode(c) !== key);

  let data = await postCartDiscount(next);
  let result = validateDiscountResponse(data, trimmed);

  // 刚加购后规则未就绪：再试同一批（有效码 + 新码）
  if (!result.ok) {
    await delay(RETRY_DELAY_MS);
    data = await postCartDiscount(next);
    result = validateDiscountResponse(data, trimmed);
  }

  // 多码组合仍失败：最后一试「仅当前商品码」（与手动单独输该码对齐）
  // 成功则接受挤掉其它码；失败必须把 prior 还回去，否则用户原有折扣白白陪葬
  if (!result.ok && prior.length) {
    await delay(RETRY_DELAY_MS);
    data = await postCartDiscount([trimmed]);
    result = validateDiscountResponse(data, trimmed);

    if (!result.ok) {
      const soleReason = result.reason;
      await delay(RETRY_DELAY_MS);
      await postCartDiscount(prior);
      // 车已还原成不含本码的状态，别再让后面的滞后重查误判成功
      trackAutoDiscountGa4({ isSuccess: false, coupon: trimmed, reason: soleReason || 'not_applicable' });
      return false;
    }
  }

  // 折扣引擎偶发滞后：再拉一次 cart.js，已 applicable 则记成功
  if (!result.ok && result.reason === 'not_applicable') {
    await delay(RETRY_DELAY_MS);
    const later = await fetchCartDiscountState();
    if (later.applicableMap[key] === true) {
      clearNetworkFail(trimmed);
      trackAutoDiscountGa4({ isSuccess: true, coupon: trimmed, reason: 'applied' });
      // 世代已过期：折扣已写进服务端，但刷 UI 交给新一轮，避免旧车状态覆盖
      if (!isCurrent()) return false;
      // 折扣已在服务端生效，但手上的 sections 快照还是加购时的旧版。
      // 派发不带 sections 的事件让购物车重新拉一次，否则会「结算扣钱但界面看不到」
      document.dispatchEvent(new DiscountUpdateEvent({}, SOURCE_ID));
      return true;
    }
  }

  if (!result.ok) {
    // 不设业务冷却：下一轮加购（件数/组合变化）仍可再试
    trackAutoDiscountGa4({
      isSuccess: false,
      coupon: trimmed,
      reason: result.reason || 'not_applicable',
    });
    return false;
  }

  clearNetworkFail(trimmed);
  trackAutoDiscountGa4({ isSuccess: true, coupon: trimmed, reason: 'applied' });

  // 世代已过期：不派发 DiscountUpdateEvent，避免旧车状态 morph 覆盖新一轮
  if (!isCurrent()) return false;

  document.dispatchEvent(new DiscountUpdateEvent(data, SOURCE_ID));
  return true;
}

/**
 * @param {() => Promise<void>} job
 */
function enqueue(job) {
  queueTail = queueTail
    .then(job)
    .catch((error) => {
      console.error('[ug-auto-discount]', error);
    });
  return queueTail;
}

/**
 * @param {Event} event
 */
function handleCartAddCapture(event) {
  if (!isEnabled()) return;

  const detail = /** @type {any} */ (event).detail;
  if (detail?.data?.didError) return;

  const source = detail?.data?.source;
  const isAdd = event instanceof CartAddEvent || source === 'product-form-component';
  if (!isAdd) return;

  const target = event.target instanceof Element ? event.target : null;
  if (!collectAddCodes(detail, target).length) return;

  getBridge().holdDrawer = true;
}

/**
 * @param {Event} event
 */
function handleCartUpdate(event) {
  if (!isEnabled()) return;

  const detail = /** @type {any} */ (event).detail;
  if (detail?.data?.didError) return;

  const source = detail?.data?.source;
  const isAdd = event instanceof CartAddEvent || source === 'product-form-component';
  const target = event.target instanceof Element ? event.target : null;
  const codes = isAdd ? collectAddCodes(detail, target) : [];

  // hold 期间任何不是「本次带码加购」引起的车变更（改数量、删行、无码加购）都要打脏标记：
  // 手上的 cartAddSections 是加购那一刻的快照，morph 回去会把这些改动整体回滚。
  // 本模块只派发 discount:update、从不派发 cart:update，所以这里不需要按 sourceId 自我过滤。
  if (!codes.length) {
    if (getBridge().holdDrawer) markHoldCartDirty();
    return;
  }
  if (!getBridge().holdDrawer) return;

  // 本轮加购车内容已变，清掉这些码的网络冷却，允许重试
  codes.forEach((code) => clearNetworkFail(code));

  /** @type {Record<string, string> | undefined} */
  const cartAddSections = detail?.data?.sections;
  const gen = ++holdGeneration;

  // 新一轮 hold 从干净状态开始：本轮的 cartAddSections 已经包含了此前所有用户改动
  clearHoldCartDirty();
  holdAddToCartButton(target);

  // 超时兜底一旦放开 hold，用户就能继续改购物车了，之后再用旧快照 morph 会造成回滚
  let fallbackFired = false;

  const holdFallback = window.setTimeout(() => {
    if (gen !== holdGeneration) return;
    if (!getBridge().holdDrawer) return;
    fallbackFired = true;
    // 折扣还没回来：先把抽屉开出来给反馈，带码成功后队列任务会再 morph 一次。
    // 这里不清 dirty 标记，否则会吞掉 hold 期间用户的改动。
    refreshCartForHoldEnd(cartAddSections, true).catch((error) => console.error('[ug-auto-discount]', error));
    releaseHoldAndOpen();
  }, HOLD_TIMEOUT_MS);

  enqueue(async () => {
    // 排队中的任务不整体跳过：世代过期只说明「别人接管了 UI」，码该带还得带
    const isCurrent = () => gen === holdGeneration;
    try {
      const refreshedByDiscount = await applyProductCodes(codes, { isCurrent });
      if (!isCurrent()) return;

      // 带码成功时 DiscountUpdateEvent 已经刷过 UI；但 hold 期间车若被改过仍要再拉一次最新 section
      if (!refreshedByDiscount || getBridge().dirtyDuringHold) {
        await refreshCartForHoldEnd(cartAddSections, !fallbackFired);
      }
    } catch (error) {
      console.error('[ug-auto-discount]', error);
      codes.forEach((code) => markNetworkFail(code));
      if (isCurrent()) {
        trackAutoDiscountGa4({ isSuccess: false, coupon: codes.join(','), reason: 'network_error' });
        await refreshCartForHoldEnd(cartAddSections, !fallbackFired);
      }
    } finally {
      // 自己排的定时器无论如何都要清掉
      window.clearTimeout(holdFallback);
      if (isCurrent()) {
        clearHoldCartDirty();
        releaseHoldAndOpen();
      }
    }
  });
}

function initUrlParamDiscount() {
  if (!isEnabled()) return;
  const params = new URLSearchParams(window.location.search);
  const code = (params.get('discount') || params.get('ug_discount') || '').trim();
  if (!code) return;

  enqueue(async () => {
    try {
      await applyProductCode(code, { force: true });
    } catch (error) {
      console.error('[ug-auto-discount]', error);
      markNetworkFail(code);
      trackAutoDiscountGa4({ isSuccess: false, coupon: code, reason: 'network_error' });
    }
  });
}

function init() {
  if (!isEnabled()) return;

  // Buy Now 不经过 CartAddEvent，需要在跳结账前自己把码写到车上，复用同一套 merge / 重试 / 回滚
  getBridge().applyCodes = (/** @type {string[]} */ codes) => applyProductCodes(codes, { force: true });
  document.addEventListener(ThemeEvents.cartUpdate, handleCartAddCapture, true);
  document.addEventListener(ThemeEvents.cartUpdate, handleCartUpdate);
  initUrlParamDiscount();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
